import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { Product, CartState, CartAction } from '../types';

const CartContext = createContext<{
  state: CartState;
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  increment: (productId: string) => void;
  decrement: (productId: string) => void;
  clear: () => void;
  totalItems: number;
  subtotalCents: number;
} | null>(null);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD': {
      const existing = state.items[action.product.id];
      const nextQty = (existing?.quantity ?? 0) + (action.quantity ?? 1);
      return {
        items: {
          ...state.items,
          [action.product.id]: { product: action.product, quantity: nextQty },
        },
      };
    }
    case 'REMOVE': {
      const { [action.productId]: _, ...rest } = state.items;
      return { items: rest };
    }
    case 'INCREMENT': {
      const existing = state.items[action.productId];
      if (!existing) return state;
      return {
        items: {
          ...state.items,
          [action.productId]: { ...existing, quantity: existing.quantity + 1 },
        },
      };
    }
    case 'DECREMENT': {
      const existing = state.items[action.productId];
      if (!existing) return state;
      const nextQty = existing.quantity - 1;
      if (nextQty <= 0) {
        const { [action.productId]: _, ...rest } = state.items;
        return { items: rest };
      }
      return {
        items: {
          ...state.items,
          [action.productId]: { ...existing, quantity: nextQty },
        },
      };
    }
    case 'CLEAR':
      return { items: {} };
    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: {} });

  const addToCart = (product: Product, quantity = 1) =>
    dispatch({ type: 'ADD', product, quantity });
  const removeFromCart = (productId: string) => dispatch({ type: 'REMOVE', productId });
  const increment = (productId: string) => dispatch({ type: 'INCREMENT', productId });
  const decrement = (productId: string) => dispatch({ type: 'DECREMENT', productId });
  const clear = () => dispatch({ type: 'CLEAR' });

  const { totalItems, subtotalCents } = useMemo(() => {
    let count = 0;
    let subtotal = 0;
    for (const item of Object.values(state.items)) {
      count += item.quantity;
      subtotal += item.product.priceCents * item.quantity;
    }
    return { totalItems: count, subtotalCents: subtotal };
  }, [state.items]);

  const value = useMemo(
    () => ({ state, addToCart, removeFromCart, increment, decrement, clear, totalItems, subtotalCents }),
    [state, totalItems, subtotalCents]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
